/**
 * Shared Axios client configured for the Budy frontend.
 *
 * - Sets the API base URL from `VITE_API_BASE_URL` (fallback `/api/v1`)
 * - Relies on HTTP-only cookies for auth (no tokens in browser storage)
 * - Handles 401/403 token errors with refresh and logout
 */
import axios from "axios";
import { toast } from "sonner";
import settings from "../settings";
import { refreshToken, clearAuthState } from "../store/authAtoms";
import { isTokenExpiredOrNearExpiry } from "../utils/authStorage";

// Error codes that indicate token-related issues (should trigger refresh/logout)
const TOKEN_ERROR_CODES = ["INVALID_TOKEN", "MISSING_TOKEN", "USER_NOT_FOUND"];

// MFA error codes – redirect to MFA flow instead of refreshing or logging out
const MFA_ERROR_CODES = ["MFA_SETUP_REQUIRED", "MFA_CHALLENGE_REQUIRED"];

const baseURL =
  settings.baseUrl && settings.baseUrl.trim().length > 0
    ? settings.baseUrl
    : "/api/v1";

const ABSOLUTE_URL_RE = /^https?:\/\//i;

const removeAuthorizationHeader = (headers: unknown) => {
  if (!headers || typeof headers !== "object") return;

  const axiosHeaders = headers as {
    delete?: (name: string) => void;
    Authorization?: unknown;
    authorization?: unknown;
  };
  if (typeof axiosHeaders.delete === "function") {
    axiosHeaders.delete("Authorization");
    axiosHeaders.delete("authorization");
    return;
  }
  delete axiosHeaders.Authorization;
  delete axiosHeaders.authorization;
};

export const apiClient = axios.create({
  baseURL,
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    const token = localStorage.getItem("token");
    if (token && !isTokenExpiredOrNearExpiry(token)) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      removeAuthorizationHeader(config.headers);
    }
  }
  return config;
});

export const CF_API_BASE = (apiClient.defaults.baseURL as string).replace(
  /\/+$/,
  "",
);

/** Path must start with `/` (e.g. `/cashflow`, `/transactions`). */
export function cfApi(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${CF_API_BASE}${p}`;
}

/** Normalize SWR/cfApi keys into a path axios can combine with baseURL once. */
function resolveCfRequestUrl(pathOrUrl: string): string {
  if (ABSOLUTE_URL_RE.test(pathOrUrl)) {
    return pathOrUrl;
  }

  const normalizedBase = CF_API_BASE.replace(/\/+$/, "");
  let path = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;

  if (path === normalizedBase) {
    return "/";
  }
  if (path.startsWith(`${normalizedBase}/`)) {
    path = path.slice(normalizedBase.length);
  }

  return path.startsWith("/") ? path : `/${path}`;
}

const toBodyInit = (data: unknown): BodyInit | null => {
  if (data == null) return null;
  if (
    typeof data === "string" ||
    data instanceof Blob ||
    data instanceof FormData ||
    data instanceof URLSearchParams ||
    data instanceof ArrayBuffer ||
    ArrayBuffer.isView(data)
  ) {
    return data as BodyInit;
  }
  return JSON.stringify(data);
};

const toResponse = (params: {
  data: unknown;
  status: number;
  statusText: string;
  headers?: Record<string, string>;
}): Response =>
  new Response(toBodyInit(params.data), {
    status: params.status,
    statusText: params.statusText,
    headers: params.headers,
  });

export async function cfFetch(
  pathOrUrl: string,
  init?: RequestInit,
): Promise<Response> {
  const url = resolveCfRequestUrl(pathOrUrl);

  try {
    const response = await apiClient.request({
      url,
      method: init?.method,
      data: init?.body,
      headers: init?.headers as Record<string, string> | undefined,
      signal: init?.signal,
    });
    return toResponse({
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers as Record<string, string> | undefined,
    });
  } catch (error) {
    const axiosLikeError = error as {
      response?: {
        data?: unknown;
        status?: number;
        statusText?: string;
        headers?: Record<string, string>;
      };
    };
    if (axiosLikeError.response) {
      return toResponse({
        data: axiosLikeError.response.data,
        status: axiosLikeError.response.status ?? 500,
        statusText: axiosLikeError.response.statusText ?? "Request failed",
        headers: axiosLikeError.response.headers,
      });
    }
    throw error;
  }
}

export const cfFetcher = (url: string) => cfFetch(url).then((r) => r.json());

/**
 * Checks if the error response contains a token-related error code
 */
const isTokenError = (error: unknown): boolean => {
  const detail = (
    error as { response?: { data?: { detail?: { code?: string } } } }
  )?.response?.data?.detail;
  if (typeof detail === "object" && detail?.code) {
    return TOKEN_ERROR_CODES.includes(detail.code);
  }
  return false;
};

const extractErrorMessage = (detail: unknown): string | null => {
  if (typeof detail === "string") {
    return detail.trim();
  }
  if (!detail || typeof detail !== "object") {
    return null;
  }

  const detailObject = detail as {
    message?: unknown;
    error?: unknown;
    reason?: unknown;
  };
  const candidates = [
    detailObject.message,
    detailObject.error,
    detailObject.reason,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }
  return null;
};

const isTrialUnavailableError = (error: unknown): boolean => {
  const response = (
    error as { response?: { status?: number; data?: { detail?: unknown } } }
  )?.response;
  if (response?.status !== 403) return false;

  const detailMessage = extractErrorMessage(response?.data?.detail);
  if (!detailMessage) return false;
  return detailMessage.toLowerCase().includes("trial");
};

const showTrialUnavailableToast = () => {
  toast.error("Not available on trial.", {
    action: {
      label: "View pricing",
      onClick: () => {
        if (window.location.pathname !== "/pricing") {
          window.history.pushState({}, "", "/pricing");
          window.dispatchEvent(new PopStateEvent("popstate"));
        }
      },
    },
  });
};

// Handle 401 errors by refreshing the token and retrying the request
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshToken();

        if (newToken) {
          // Access token is now stored in HTTP-only cookie; just retry the request
          return apiClient(originalRequest);
        } else {
          // Refresh returned null - user must reconnect
          await clearAuthState();
          window.location.href = "/login";
        }
      } catch (refreshError) {
        console.error("Failed to refresh token on 401 error:", refreshError);
        // refreshToken throws on failure and clears auth state, but ensure logout
        await clearAuthState();
        window.location.href = "/login";
      }
    }

    // Handle 403 MFA errors – redirect to /mfa without refresh loop
    if (status === 403 || status === 429) {
      const detail = error?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : undefined;
      if (code && MFA_ERROR_CODES.includes(code)) {
        window.location.href = "/mfa";
        return Promise.reject(error);
      }
    }

    if (isTrialUnavailableError(error)) {
      showTrialUnavailableToast();
      return Promise.reject(error);
    }

    // Handle 403 errors - only for token-related error codes
    if (status === 403 && isTokenError(error) && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshToken();

        if (newToken) {
          // Access token is now stored in HTTP-only cookie; just retry the request
          const retryResponse = await apiClient(originalRequest);
          return retryResponse;
        } else {
          // Refresh returned null - user must reconnect
          await clearAuthState();
          window.location.href = "/login";
        }
      } catch (refreshError) {
        console.error("Failed to refresh token on 403 error:", refreshError);
        // refreshToken throws on failure and clears auth state, but ensure logout
        await clearAuthState();
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
